import { useState } from "react";

import { DotsLoader, Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";

/* eslint-disable max-len */
import KeywordGeneratorToolPageQuestionsTable from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageQuestionsTable";
import KeywordGeneratorToolPageRelatedTable from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageRelatedTable";
import KeywordGeneratorToolPageTable from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageTable";
import KeywordGeneratorToolPageTrendingTable from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageTrendingTable";
/* eslint-enable max-len */
import { TableWrap } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/styledComponents";
import { i18nFilter } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { KeywordGeneratorToolNoDataForLargeKeyword } from "./KeywordGeneratorToolNoDataForLargeKeyword";
import { EKWGeneratorTableTabs, tabsIndexMap } from "app/common/keyword-research/keyword-generator/types";

const MAX_WORDS_IN_KEYWORD = 10;

export const KeywordGeneratorToolResultsTable = (props: any) => {
    const { onKeywordsAddedToGroup = () => null, notify = false, selectedTab, params } = props;
    const [titleData, setTitleData] = useState({
        totalRows: -1,
        totalRelatedRows: -1,
        totalTrendingRows: -1,
        totalQuestionsRows: -1,
    });
    const i18n = i18nFilter();
    const isAllowedMaxWordsInKeyword = props.params?.keyword?.split(" ").length <= MAX_WORDS_IN_KEYWORD;
    const onTabSelect = (selectedTabIndex: number) => {
        let tab = "";
        switch (selectedTabIndex) {
            case tabsIndexMap[EKWGeneratorTableTabs.relatedKeywords]:
                tab = "RELATED KEYWORDS";
                break;
            case tabsIndexMap[EKWGeneratorTableTabs.trending]:
                tab = "TRENDING KEYWORDS";
                break;
            case tabsIndexMap[EKWGeneratorTableTabs.questions]:
                tab = "QUESTION QUERIES";
                break;
            case tabsIndexMap[EKWGeneratorTableTabs.phraseMatch]:
            default:
                tab = "PHRASE-MATCH";
                break;
        }

        allTrackers.trackEvent("Tab", "Switch", tab);
        props.onTabSelect &&
            props.onTabSelect(Object.keys(tabsIndexMap).find((key) => tabsIndexMap[key] === selectedTabIndex));
    };
    const createDataCallback =
        (tab) =>
        ({ totalRecords }) => {
            setTitleData((titleData) => ({ ...titleData, [tab]: totalRecords }));
        };
    const onDataError = (tab, err) => {
        setTitleData((titleData) => ({ ...titleData, [tab]: null }));
    };
    const getDataCallback = createDataCallback("totalRows");
    const getDataCallbackRelated = createDataCallback("totalRelatedRows");
    const getDataCallbackTrending = createDataCallback("totalTrendingRows");
    const getDataCallbackQuestions = createDataCallback("totalQuestionsRows");

    const getTabTitle = (id, stateParam) => {
        if (!isAllowedMaxWordsInKeyword) {
            return (
                <FlexRow>
                    {i18n(`keyword.generator.tool.page.tab.${id}.title`, {
                        total: null,
                    })}
                </FlexRow>
            );
        }
        return (
            <div style={{ display: "flex", alignItems: "center" }}>
                {i18n(`keyword.generator.tool.page.tab.${id}.title`, {
                    total: titleData[stateParam] > 0 ? `(${numberFilter()(titleData[stateParam])})` : "",
                })}
                {titleData[stateParam] === -1 ? (
                    <div style={{ marginLeft: "55px" }}>
                        <DotsLoader />
                    </div>
                ) : null}
            </div>
        );
    };
    return (
        <TableWrap>
            <Tabs
                onSelect={onTabSelect}
                forceRenderTabPanel={true}
                defaultIndex={tabsIndexMap[selectedTab]}
                selectedIndex={tabsIndexMap[selectedTab]}
            >
                <TabList>
                    <Tab
                        className="firstTab"
                        tooltipText={i18n("keyword.generator.tool.page.tab.phrase.tooltip")}
                        key="tab-0"
                    >
                        {getTabTitle("phrase", "totalRows")}
                    </Tab>
                    <Tab
                        className="secondTab"
                        tooltipText={i18n("keyword.generator.tool.page.tab.related.tooltip")}
                        key="tab-1"
                    >
                        {getTabTitle("related", "totalRelatedRows")}
                    </Tab>
                    <Tab
                        tooltipText={i18n("keyword.generator.tool.page.tab.trending.tooltip")}
                        key="tab-2"
                    >
                        {getTabTitle("trending", "totalTrendingRows")}
                    </Tab>
                    <Tab
                        tooltipText={i18n("keyword.generator.tool.page.tab.questions.tooltip")}
                        key="tab-3"
                    >
                        {getTabTitle("questions", "totalQuestionsRows")}
                    </Tab>
                </TabList>
                <TabPanel className="firstTab">
                    {isAllowedMaxWordsInKeyword ? (
                        <KeywordGeneratorToolPageTable
                            {...params}
                            isTableInView={selectedTab === EKWGeneratorTableTabs.phraseMatch}
                            onDataError={(err) => onDataError("totalRows", err)}
                            getDataCallback={getDataCallback}
                            onKeywordsAddedToGroup={onKeywordsAddedToGroup}
                            notify={notify}
                        />
                    ) : (
                        <KeywordGeneratorToolNoDataForLargeKeyword />
                    )}
                </TabPanel>
                <TabPanel className="secondTab">
                    {isAllowedMaxWordsInKeyword ? (
                        <KeywordGeneratorToolPageRelatedTable
                            {...params}
                            isTableInView={selectedTab === EKWGeneratorTableTabs.relatedKeywords}
                            onDataError={(err) => onDataError("totalRelatedRows", err)}
                            getDataCallback={getDataCallbackRelated}
                            onKeywordsAddedToGroup={onKeywordsAddedToGroup}
                            notify={notify}
                        />
                    ) : (
                        <KeywordGeneratorToolNoDataForLargeKeyword />
                    )}
                </TabPanel>
                <TabPanel className="thirdTab">
                    {isAllowedMaxWordsInKeyword ? (
                        <KeywordGeneratorToolPageTrendingTable
                            {...params}
                            isTableInView={selectedTab === EKWGeneratorTableTabs.trending}
                            onDataError={(err) => onDataError("totalTrendingRows", err)}
                            getDataCallback={getDataCallbackTrending}
                            onKeywordsAddedToGroup={onKeywordsAddedToGroup}
                            notify={notify}
                        />
                    ) : (
                        <KeywordGeneratorToolNoDataForLargeKeyword />
                    )}
                </TabPanel>
                <TabPanel className="fourthTab">
                    {isAllowedMaxWordsInKeyword ? (
                        <KeywordGeneratorToolPageQuestionsTable
                            {...params}
                            isTableInView={selectedTab === EKWGeneratorTableTabs.questions}
                            onDataError={(err) => onDataError("totalQuestionsRows", err)}
                            getDataCallback={getDataCallbackQuestions}
                            onKeywordsAddedToGroup={onKeywordsAddedToGroup}
                            notify={notify}
                        />
                    ) : (
                        <KeywordGeneratorToolNoDataForLargeKeyword />
                    )}
                </TabPanel>
            </Tabs>
        </TableWrap>
    );
};
